// Recovery for React DOM-mutation errors caused by browser page translation
// (Google Translate wraps text nodes in <font>, so React's removeChild /
// insertBefore hit nodes that are no longer where React expects them).
// See GooAutoTranslate.md.

const RELOAD_KEY = 'vt_dom_error_reloads'

// Reload loop: Chrome re-translates the page right after a reload and React
// crashes again during load. Such a crash happens within the first seconds.
const LOOP_WINDOW_MS = 30_000
const LOOP_PAGE_AGE_MS = 3_000
// Hard cap per page, whatever the timing looks like.
const CAP_WINDOW_MS = 5 * 60_000
const CAP_MAX_RELOADS = 5

type ReloadEntry = { t: number; p: string }

export function isDomMutationError(error: { name?: string; message?: string }): boolean {
    const msg = error.message || ''
    return (
        error.name === 'NotFoundError' ||
        /removeChild|insertBefore|not a child of this node/i.test(msg)
    )
}

// Returns true (and records the attempt) if a reload is allowed right now.
//
// The history is tracked PER PAGE (pathname): a reload on /cart says nothing
// about /checkout. For the current page a reload is not allowed when:
//  - the previous auto-reload of this same page was < 30s ago AND it crashed
//    again within 3s of loading (that is a reload loop), or
//  - there were already 5 auto-reloads of this page in the last 5 minutes.
export function claimAutoReload(): boolean {
    try {
        const now = Date.now()
        const path = window.location.pathname
        const raw = sessionStorage.getItem(RELOAD_KEY)
        const parsed: unknown = raw ? JSON.parse(raw) : []
        const all = (Array.isArray(parsed) ? parsed : []).filter(
            (e): e is ReloadEntry =>
                !!e && typeof e.t === 'number' && typeof e.p === 'string' && now - e.t < CAP_WINDOW_MS
        )
        const forPage = all.filter((e) => e.p === path)

        if (forPage.length >= CAP_MAX_RELOADS) return false

        const last = forPage[forPage.length - 1]
        const crashedRightAfterLoad = performance.now() < LOOP_PAGE_AGE_MS
        if (last && now - last.t < LOOP_WINDOW_MS && crashedRightAfterLoad) return false

        all.push({ t: now, p: path })
        sessionStorage.setItem(RELOAD_KEY, JSON.stringify(all))
        return true
    } catch {
        // sessionStorage unavailable: no way to guard against a loop, so don't reload
        return false
    }
}
